/**
 * Flow editor / manager prototype (?test=flow-editor)
 * Scenario-first v2: operator workflow, routing incidents, run-aware detail pane.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  ArrowUpRight,
  BookOpen,
  Bot,
  ChevronDown,
  ChevronRight,
  Inbox,
  Mic,
  RefreshCw,
  Route,
  Sparkles,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

type FlowTier = 'workspace_locked' | 'user_scoped'
type WorkspaceId = 'hop' | 'personal' | 'user'
type RunStatus = 'ok' | 'warning' | 'error'

interface FlowRun {
  id: string
  startedAt: string
  status: RunStatus
  routedTo: WorkspaceId
  source: string
  sourceThreadId?: string
  summary: string
  steps: Array<{
    label: string
    status: 'done' | 'skipped' | 'error'
    detail?: string
  }>
}

interface FlowDefinition {
  id: string
  name: string
  tier: FlowTier
  trigger: string
  executionEnabled: boolean
  workspaceLock?: Exclude<WorkspaceId, 'user'>
  defaultOutput: WorkspaceId
  ownerLabel: string
  lastRunAt: string
  nextRunAt: string
  health: RunStatus
  routingStrategy: string
  scenario: string
}

const FLOW_DEFINITIONS: FlowDefinition[] = [
  {
    id: 'daily-digest',
    name: 'Daily digest',
    tier: 'workspace_locked',
    trigger: 'Basecamp assignments + schedule (09:00)',
    executionEnabled: true,
    workspaceLock: 'hop',
    defaultOutput: 'hop',
    ownerLabel: 'Hop Studios',
    lastRunAt: 'Today 09:01',
    nextRunAt: 'Tomorrow 09:00',
    health: 'ok',
    routingStrategy: 'Locked to Hop (extension scope)',
    scenario: 'Workspace-locked baseline',
  },
  {
    id: 'voice-intake-router',
    name: 'Voice memo intake router',
    tier: 'user_scoped',
    trigger: 'New voice memo',
    executionEnabled: true,
    defaultOutput: 'user',
    ownerLabel: 'User-scoped',
    lastRunAt: '43m ago',
    nextRunAt: 'Event-driven',
    health: 'warning',
    routingStrategy: 'Classifier routes to workspace; fallback to User + needs-routing',
    scenario: 'User-scoped with dynamic routing',
  },
  {
    id: 'email-forward-router',
    name: 'Forwarded email router',
    tier: 'user_scoped',
    trigger: 'Inbound @in.flownotes.app forward',
    executionEnabled: true,
    defaultOutput: 'user',
    ownerLabel: 'User-scoped',
    lastRunAt: '2h ago',
    nextRunAt: 'Event-driven',
    health: 'error',
    routingStrategy: 'Domain and keyword matcher with manual fallback queue',
    scenario: 'Incident recovery path',
  },
]

const RUNS_BY_FLOW: Record<string, FlowRun[]> = {
  'daily-digest': [
    {
      id: 'dd-112',
      startedAt: 'Today 09:01',
      status: 'ok',
      routedTo: 'hop',
      source: 'Scheduled trigger',
      summary: 'Posted digest thread to Hop inbox.',
      steps: [
        { label: 'Fetch overdue Basecamp assignments', status: 'done', detail: '4 assignments' },
        { label: 'Pull updates from STATUS-BOARD', status: 'done', detail: '3 bet movements' },
        { label: 'Generate digest summary', status: 'done', detail: '184 tokens' },
        { label: 'Create inbox thread (kind=flow)', status: 'done', detail: 'Thread id: dg1' },
      ],
    },
  ],
  'voice-intake-router': [
    {
      id: 'vm-084',
      startedAt: '43m ago',
      status: 'warning',
      routedTo: 'user',
      source: 'Voice memo: Walk to coffee shop',
      sourceThreadId: 'v2',
      summary: 'Classifier confidence low. Routed to User inbox with needs-routing tag.',
      steps: [
        { label: 'Transcribe memo', status: 'done', detail: '2m15s transcript' },
        { label: 'Extract project entities', status: 'done', detail: 'No high-confidence client slug' },
        { label: 'Route workspace', status: 'error', detail: 'Confidence 0.42 below threshold 0.65' },
        { label: 'Fallback: user inbox + needs-routing', status: 'done', detail: 'Awaiting manual triage' },
      ],
    },
    {
      id: 'vm-083',
      startedAt: 'Yesterday',
      status: 'ok',
      routedTo: 'hop',
      source: 'Voice memo: GGSC monthly planning',
      summary: 'Matched GGSC keywords and routed to Hop.',
      steps: [
        { label: 'Transcribe memo', status: 'done' },
        { label: 'Classify workspace intent', status: 'done', detail: 'Matched ggsc + basecamp' },
        { label: 'Create thread in Hop workspace', status: 'done', detail: 'Thread id: vm-hop-19' },
      ],
    },
  ],
  'email-forward-router': [
    {
      id: 'em-204',
      startedAt: '2h ago',
      status: 'error',
      routedTo: 'user',
      source: 'Forward from unknown domain',
      sourceThreadId: 'e1',
      summary: 'No domain rule match and no content fallback. Escalated to incident queue.',
      steps: [
        { label: 'Parse sender and recipients', status: 'done', detail: 'From: external-consulting.dev' },
        { label: 'Match domain rule', status: 'error', detail: 'No rule found' },
        { label: 'Keyword classifier fallback', status: 'error', detail: 'No confidence > 0.50' },
        { label: 'Escalate to needs-routing queue', status: 'done', detail: 'Incident id: route-77' },
      ],
    },
  ],
}

const RECIPES = [
  { name: 'Basecamp todo to work spec', category: 'Extension locked', workspace: 'Hop implied' },
  { name: 'Daily digest to flow thread', category: 'Extension locked', workspace: 'Hop implied' },
  { name: 'Voice memo classify and route', category: 'User-scoped', workspace: 'Dynamic route' },
  { name: 'Email forward classify and route', category: 'User-scoped', workspace: 'Dynamic route' },
]

function parseQuery() {
  const params = new URLSearchParams(window.location.search)
  return {
    flow: params.get('flow') ?? '',
    run: params.get('run') ?? '',
    sourceThread: params.get('sourceThread') ?? '',
  }
}

function healthBadge(status: RunStatus) {
  if (status === 'ok') return <Badge className="bg-green-500/10 text-green-500 border border-green-500/20">Healthy</Badge>
  if (status === 'warning') return <Badge className="bg-yellow-500/10 text-yellow-600 border border-yellow-500/20">Needs review</Badge>
  return <Badge className="bg-red-500/10 text-red-500 border border-red-500/20">Incident</Badge>
}

function workspaceLabel(id: WorkspaceId) {
  if (id === 'hop') return 'Hop Studios'
  if (id === 'personal') return 'Personal'
  return 'User (unscoped)'
}

function StepLog({ run }: { run: FlowRun }) {
  const [expanded, setExpanded] = useState(true)
  return (
    <div className="border border-border" data-testid={testIds.flowEditorPrototype.jobHistory}>
      <button
        type="button"
        className={cn(
          'flex items-center gap-2 w-full px-3 py-2 bg-muted/30 hover:bg-muted/40 text-left',
          expanded && 'border-b border-border',
        )}
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
        <span className="text-xs font-medium flex-1">Execution steps ({run.steps.length})</span>
        <span className="text-[10px] text-muted-foreground">
          {run.steps.filter(s => s.status === 'done').length}/{run.steps.length} done
        </span>
      </button>
      {expanded && (
        <div className="divide-y divide-border">
          {run.steps.map((step, idx) => (
            <div key={`${run.id}-step-${idx}`} className="px-3 py-2 flex items-start gap-2.5">
              <span
                className={cn(
                  'mt-0.5 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0',
                  step.status === 'done' && 'bg-green-500/20 text-green-400',
                  step.status === 'skipped' && 'bg-muted text-muted-foreground',
                  step.status === 'error' && 'bg-red-500/20 text-red-400',
                )}
              >
                {step.status === 'done' ? '\u2713' : step.status === 'error' ? '!' : '\u2013'}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-foreground leading-snug">{step.label}</p>
                {step.detail && <p className="text-[10px] text-muted-foreground mt-0.5">{step.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function FlowEditorTest() {
  const [flows, setFlows] = useState(FLOW_DEFINITIONS)
  const [hopVisible, setHopVisible] = useState(true)
  const [personalVisible, setPersonalVisible] = useState(true)
  const [query, setQuery] = useState('')
  const [activeFlowId, setActiveFlowId] = useState(FLOW_DEFINITIONS[0].id)
  const [activeRunId, setActiveRunId] = useState('')
  const [incidentsExpanded, setIncidentsExpanded] = useState(false)

  const parsed = useMemo(parseQuery, [])

  useEffect(() => {
    if (!parsed.flow) return
    const q = parsed.flow.toLowerCase()
    const matched = flows.find(
      f => f.name.toLowerCase() === q || f.name.toLowerCase().includes(q) || f.id.includes(q.replace(/\s+/g, '-')),
    )
    if (matched) {
      setActiveFlowId(matched.id)
      if (parsed.run) setActiveRunId(parsed.run)
    }
  }, [flows, parsed.flow, parsed.run])

  const visibleFlows = useMemo(
    () =>
      flows.filter(flow => {
        if (flow.workspaceLock === 'hop' && !hopVisible) return false
        if (flow.workspaceLock === 'personal' && !personalVisible) return false
        const searchable = `${flow.name} ${flow.trigger} ${flow.scenario}`.toLowerCase()
        return searchable.includes(query.toLowerCase())
      }),
    [flows, hopVisible, personalVisible, query],
  )

  const activeFlow = flows.find(f => f.id === activeFlowId) ?? flows[0]
  const runs = RUNS_BY_FLOW[activeFlow.id] ?? []
  const activeRun = runs.find(r => r.id === activeRunId) ?? runs[0] ?? null

  const incidents = useMemo(() => {
    return flows.flatMap(flow => {
      const flowRuns = RUNS_BY_FLOW[flow.id] ?? []
      return flowRuns
        .filter(run => run.status !== 'ok')
        .map(run => ({ flow, run }))
    })
  }, [flows])

  const toggleExecution = (flowId: string) => {
    setFlows(prev => prev.map(flow => (flow.id === flowId ? { ...flow, executionEnabled: !flow.executionEnabled } : flow)))
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid={testIds.flowEditorPrototype.root}>
      <header className="border-b border-border px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-green-500/15 text-green-500 flex items-center justify-center shrink-0">
          <Zap className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-semibold">Flows - operator prototype v2</h1>
          <p className="text-[10px] text-muted-foreground">Scenario-first: locked flows, user-scoped routing, and incident recovery in one workflow.</p>
        </div>
        <Button variant="outline" size="sm" className="text-xs" asChild>
          <a href="/?test=index" data-testid={testIds.flowEditorPrototype.backToPrototypeIndex}>
            <BookOpen className="h-3.5 w-3.5 mr-1" /> Prototypes
          </a>
        </Button>
        <Button variant="outline" size="sm" className="text-xs" asChild>
          <a href="/?test=inbox-split" data-testid={testIds.flowEditorPrototype.backToInboxSplit}>
            <Inbox className="h-3.5 w-3.5 mr-1" /> Inbox split
          </a>
        </Button>
      </header>

      <div className="px-4 py-2 border-b border-border bg-muted/20 flex items-center gap-2 flex-wrap" data-testid={testIds.flowEditorPrototype.workspaceScopeBar}>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Workspace visibility</span>
        <button
          type="button"
          onClick={() => setHopVisible(v => !v)}
          className={cn(
            'px-2.5 py-1 rounded-full text-xs border transition-colors',
            hopVisible ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-background border-border text-muted-foreground opacity-60',
          )}
        >
          Hop Studios
        </button>
        <button
          type="button"
          onClick={() => setPersonalVisible(v => !v)}
          className={cn(
            'px-2.5 py-1 rounded-full text-xs border transition-colors',
            personalVisible ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-500' : 'bg-background border-border text-muted-foreground opacity-60',
          )}
        >
          Personal
        </button>
        <span className="text-[10px] text-muted-foreground ml-auto">View filter only. Execution remains server-side.</span>
      </div>

      {parsed.sourceThread && (
        <Alert className="mx-4 mt-3">
          <Route className="h-4 w-4" />
          <AlertTitle className="text-xs">Opened from inbox thread {parsed.sourceThread}</AlertTitle>
          <AlertDescription className="text-xs">
            You jumped here from the reader. Use the Runs tab to inspect routing and recover incidents before re-running.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="min-h-0 overflow-y-auto border-r border-border flex flex-col">
          {/* Flow inventory */}
          <div className="border-b border-border">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex-1">Flow inventory</span>
              <span className="text-[10px] text-muted-foreground">{visibleFlows.length}</span>
            </div>
            <div className="px-3 py-2 border-b border-border">
              <Input
                placeholder="Filter flows..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
            <div data-testid={testIds.flowEditorPrototype.managerTable}>
              {visibleFlows.map(flow => (
                <button
                  key={flow.id}
                  type="button"
                  onClick={() => {
                    setActiveFlowId(flow.id)
                    setActiveRunId('')
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2.5 border-b border-border border-l-2 transition-colors',
                    activeFlowId === flow.id
                      ? 'bg-primary/5 border-l-primary'
                      : 'hover:bg-muted/40 border-l-transparent',
                  )}
                >
                  <div className="flex items-center gap-2 mb-1 min-w-0">
                    <span className={cn('text-xs truncate flex-1', activeFlowId === flow.id ? 'font-semibold text-foreground' : 'text-foreground')}>
                      {flow.name}
                    </span>
                    {healthBadge(flow.health)}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-1">
                    <Badge variant="outline" className="h-4 text-[9px] px-1 leading-none">
                      {flow.tier === 'workspace_locked' ? 'Locked' : 'User-scoped'}
                    </Badge>
                    <span className="truncate">{flow.scenario}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate leading-snug">{flow.trigger}</p>
                  <p className="text-[10px] text-muted-foreground/60 truncate leading-snug">Last: {flow.lastRunAt}</p>
                </button>
              ))}
              {visibleFlows.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6 px-3">No flows match this filter.</p>
              )}
            </div>
          </div>

          {/* Incident queue (collapsible, default closed) */}
          <div className="border-b border-border">
            <button
              type="button"
              onClick={() => setIncidentsExpanded(v => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-muted/30 hover:bg-muted/40 text-left"
              aria-expanded={incidentsExpanded}
            >
              {incidentsExpanded ? (
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
              )}
              <AlertTriangle className={cn('h-3.5 w-3.5 shrink-0', incidents.length > 0 ? 'text-yellow-500' : 'text-muted-foreground/50')} />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex-1">Incident queue</span>
              {incidents.length > 0 && (
                <Badge className="bg-yellow-500/10 text-yellow-600 border border-yellow-500/20 h-4 px-1.5 text-[10px] leading-none">
                  {incidents.length}
                </Badge>
              )}
            </button>
            {incidentsExpanded && (
              <div className="border-t border-border">
                {incidents.map(({ flow, run }) => (
                  <button
                    key={run.id}
                    type="button"
                    onClick={() => {
                      setActiveFlowId(flow.id)
                      setActiveRunId(run.id)
                    }}
                    className="w-full px-3 py-2 border-b border-border border-l-2 border-l-transparent hover:bg-muted/40 hover:border-l-yellow-500/40 text-left"
                  >
                    <div className="flex items-center gap-2 mb-1 min-w-0">
                      <span className="text-xs font-medium truncate flex-1">{flow.name}</span>
                      {healthBadge(run.status)}
                    </div>
                    <p className="text-[10px] text-muted-foreground truncate leading-snug">{run.summary}</p>
                  </button>
                ))}
                {incidents.length === 0 && (
                  <p className="text-xs text-muted-foreground px-3 py-3">No active incidents.</p>
                )}
              </div>
            )}
          </div>
        </aside>

        <section className="min-h-0 overflow-y-auto flex flex-col" data-testid={testIds.flowEditorPrototype.editorPanel}>
          {/* Header strip */}
          <div className="px-4 py-3 border-b border-border flex items-start gap-3">
            <div className={cn('w-8 h-8 rounded-md flex items-center justify-center shrink-0', activeFlow.tier === 'workspace_locked' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500')}>
              {activeFlow.tier === 'workspace_locked' ? <Bot className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold leading-tight">{activeFlow.name}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {activeFlow.tier === 'workspace_locked'
                  ? `Workspace-locked to ${workspaceLabel(activeFlow.workspaceLock ?? 'hop')}`
                  : 'User-scoped. Output destination is decided per run.'}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Execution</span>
              <Switch
                checked={activeFlow.executionEnabled}
                onCheckedChange={() => toggleExecution(activeFlow.id)}
                aria-label={`Toggle ${activeFlow.name}`}
              />
            </div>
          </div>

          {/* Meta strip - 3 cells with vertical dividers */}
          <div className="grid grid-cols-3 border-b border-border text-[10px]">
            <div className="px-4 py-2 border-r border-border">
              <p className="text-muted-foreground mb-0.5 uppercase tracking-wider">Trigger</p>
              <p className="text-foreground leading-snug">{activeFlow.trigger}</p>
            </div>
            <div className="px-4 py-2 border-r border-border">
              <p className="text-muted-foreground mb-0.5 uppercase tracking-wider">Last / next</p>
              <p className="text-foreground leading-snug">{activeFlow.lastRunAt} / {activeFlow.nextRunAt}</p>
            </div>
            <div className="px-4 py-2">
              <p className="text-muted-foreground mb-0.5 uppercase tracking-wider">Routing model</p>
              <p className="text-foreground leading-snug">{activeFlow.routingStrategy}</p>
            </div>
          </div>

          <Tabs defaultValue="overview" className="w-full flex-1 flex flex-col min-h-0">
            <TabsList className="grid grid-cols-4 h-9 rounded-none border-b border-border bg-transparent p-0" data-testid={testIds.flowEditorPrototype.surfaceTabs}>
              <TabsTrigger value="overview" className="text-xs rounded-none border-r border-border data-[state=active]:bg-muted/30 data-[state=active]:shadow-none">Overview</TabsTrigger>
              <TabsTrigger value="builder" className="text-xs rounded-none border-r border-border data-[state=active]:bg-muted/30 data-[state=active]:shadow-none">Builder</TabsTrigger>
              <TabsTrigger value="runs" className="text-xs rounded-none border-r border-border data-[state=active]:bg-muted/30 data-[state=active]:shadow-none">Runs</TabsTrigger>
              <TabsTrigger value="recipes" className="text-xs rounded-none data-[state=active]:bg-muted/30 data-[state=active]:shadow-none">Recipes</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-0 p-4 space-y-3">
              <Alert className="rounded-none">
                <Sparkles className="h-4 w-4" />
                <AlertTitle className="text-xs">Operator story</AlertTitle>
                <AlertDescription className="text-xs">
                  This flow demonstrates: ingest event, classify scope, route output, and surface failures for manual triage.
                </AlertDescription>
              </Alert>

              {activeRun && (
                <div className="border border-border p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium">Latest run: {activeRun.id}</span>
                    {healthBadge(activeRun.status)}
                  </div>
                  <p className="text-xs text-muted-foreground">{activeRun.summary}</p>
                  <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span>Routed to: {workspaceLabel(activeRun.routedTo)}</span>
                    <span>Source: {activeRun.source}</span>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="builder" className="mt-0 p-4">
              <div className="border border-border">
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b border-border">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Trigger and scope</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="text-xs h-7">
                      <RefreshCw className="h-3 w-3 mr-1" /> Save draft
                    </Button>
                    <Button size="sm" className="text-xs h-7">Simulate run</Button>
                  </div>
                </div>
                <div className="p-3 text-xs space-y-2">
                  <div className="border border-border px-2.5 py-2 bg-muted/20">Trigger: {activeFlow.trigger}</div>
                  {activeFlow.tier === 'workspace_locked' ? (
                    <div className="border border-border px-2.5 py-2 bg-muted/20">
                      Workspace: {workspaceLabel(activeFlow.workspaceLock ?? 'hop')} (locked by extension)
                    </div>
                  ) : (
                    <div className="border border-dashed border-amber-500/40 px-2.5 py-2 bg-amber-500/5">
                      Routing step: classify workspace intent. Fallback to User inbox with needs-routing tag.
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="runs" className="mt-0 p-0 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] flex-1 min-h-0">
              <div className="border-r border-border">
                {(runs.length ? runs : []).map(run => (
                  <button
                    key={run.id}
                    type="button"
                    onClick={() => setActiveRunId(run.id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 border-b border-border border-l-2 transition-colors',
                      activeRun?.id === run.id
                        ? 'bg-primary/5 border-l-primary'
                        : 'hover:bg-muted/40 border-l-transparent',
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium">{run.id}</span>
                      {healthBadge(run.status)}
                    </div>
                    <p className="text-[10px] text-muted-foreground">{run.startedAt}</p>
                    <p className="text-[10px] text-muted-foreground">to {workspaceLabel(run.routedTo)}</p>
                  </button>
                ))}
              </div>
              <div className="p-4">
                {activeRun ? (
                  <div className="space-y-2">
                    <div className="border border-border p-3">
                      <p className="text-xs font-medium mb-1">Run summary</p>
                      <p className="text-xs text-muted-foreground">{activeRun.summary}</p>
                      <div className="mt-2 text-[10px] text-muted-foreground flex gap-3 flex-wrap">
                        <span>Started: {activeRun.startedAt}</span>
                        <span>Routed: {workspaceLabel(activeRun.routedTo)}</span>
                        {activeRun.sourceThreadId && <span>Thread: {activeRun.sourceThreadId}</span>}
                      </div>
                    </div>
                    <StepLog run={activeRun} />
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No runs yet for this flow.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="recipes" className="mt-0 p-4" data-testid={testIds.flowEditorPrototype.recipeGallery}>
              <div className="grid gap-2 sm:grid-cols-2">
                {RECIPES.map(recipe => (
                  <div key={recipe.name} className="border border-border p-3">
                    <p className="text-xs font-medium leading-tight">{recipe.name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-[10px]">{recipe.category}</Badge>
                      <span className="text-[10px] text-muted-foreground">{recipe.workspace}</span>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 text-xs w-full mt-2.5">
                      Use recipe <ArrowUpRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  )
}
